import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import moment from 'moment-jalaali';
import LoadingSpinner from '../components/common/LoadingSpinner';
import WatchdogDayModal, { DayDetailData, DayOriginRect } from '../components/WatchdogDayModal';
import WatchdogMonthModal, { MonthDetailData } from '../components/WatchdogMonthModal';
import WatchdogViewSwitcher, { WatchdogViewMode } from '../components/WatchdogViewSwitcher';
import WatchdogDayTooltip, {
  WatchdogDayStats,
  WatchdogTooltipState,
} from '../components/WatchdogDayTooltip';
import { getApiBaseUrl } from '../utils/getApiUrl';
import { canAccessCustomerClub } from '../utils/customerClubAccess';

const MONTH_NAMES = [
  'فروردین',
  'اردیبهشت',
  'خرداد',
  'تیر',
  'مرداد',
  'شهریور',
  'مهر',
  'آبان',
  'آذر',
  'دی',
  'بهمن',
  'اسفند',
];

const WEEKDAYS = ['ش', 'ی', 'د', 'س', 'چ', 'پ', 'ج'];

const toPersianDigits = (s: string | number): string =>
  String(s).replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)]);

interface WatchdogResponse {
  year: number;
  today: string;
  earliest_year: number;
  current_year: number;
  total: number;
  total_naghdi?: number;
  total_bime?: number;
  total_amount?: number;
  peak_day?: string | null;
  peak_count: number;
  days: Record<string, WatchdogDayStats | number>;
}

const emptyDayStats = (): WatchdogDayStats => ({
  count: 0,
  naghdi: 0,
  bime: 0,
  total_amount: 0,
});

const normalizeDayStats = (value: WatchdogDayStats | number | undefined): WatchdogDayStats => {
  if (value == null) return emptyDayStats();
  if (typeof value === 'number') {
    return { count: value, naghdi: 0, bime: 0, total_amount: 0 };
  }
  return {
    count: Number(value.count) || 0,
    naghdi: Number(value.naghdi) || 0,
    bime: Number(value.bime) || 0,
    total_amount: Number(value.total_amount) || Number(value.naghdi || 0) + Number(value.bime || 0),
  };
};

type Cell =
  | { kind: 'empty' }
  | { kind: 'day'; date: string; day: number; count: number; isToday: boolean; isFuture: boolean };

const jalaliDayKey = (year: number, month: number, day: number): string =>
  `${year}/${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}`;

const buildMonthCells = (
  year: number,
  monthIndex: number,
  dayStats: Record<string, WatchdogDayStats>,
  today: string
): Cell[] => {
  const month = monthIndex + 1;
  const start = moment().jYear(year).jMonth(monthIndex).jDate(1).startOf('day');
  const daysInMonth = start.clone().endOf('jMonth').jDate();
  // شنبه اول هفته: Sat=6 → 0
  const startOffset = (start.day() + 1) % 7;
  const cells: Cell[] = [];
  for (let i = 0; i < startOffset; i += 1) {
    cells.push({ kind: 'empty' });
  }
  for (let d = 1; d <= daysInMonth; d += 1) {
    const date = jalaliDayKey(year, month, d);
    cells.push({
      kind: 'day',
      date,
      day: d,
      count: dayStats[date]?.count ?? 0,
      isToday: date === today,
      isFuture: date > today,
    });
  }
  while (cells.length % 7 !== 0) {
    cells.push({ kind: 'empty' });
  }
  return cells;
};

const heatStyle = (count: number, max: number): React.CSSProperties | undefined => {
  if (count <= 0 || max <= 0) return undefined;
  const t = Math.min(1, count / max);
  const alpha = 0.12 + t * 0.55;
  return { backgroundColor: `rgba(37, 99, 235, ${alpha})` };
};

type ViewMode = WatchdogViewMode;

const aggregateMonthStats = (
  dayStats: Record<string, WatchdogDayStats>,
  year: number
): Record<number, WatchdogDayStats> => {
  const out: Record<number, WatchdogDayStats> = {};
  for (let m = 1; m <= 12; m += 1) {
    const lastDay = moment().jYear(year).jMonth(m - 1).jDate(1).endOf('jMonth').jDate();
    let count = 0;
    let naghdi = 0;
    let bime = 0;
    for (let d = 1; d <= lastDay; d += 1) {
      const stats = dayStats[jalaliDayKey(year, m, d)];
      if (!stats) continue;
      count += stats.count;
      naghdi += stats.naghdi;
      bime += stats.bime;
    }
    out[m] = { count, naghdi, bime, total_amount: naghdi + bime };
  }
  return out;
};

const AdminCustomerClubWatchdog: React.FC = () => {
  const navigate = useNavigate();
  const [year, setYear] = useState(() => moment().jYear());
  const [data, setData] = useState<WatchdogResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tip, setTip] = useState<WatchdogTooltipState | null>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [dayOrigin, setDayOrigin] = useState<DayOriginRect | null>(null);
  const [dayIsToday, setDayIsToday] = useState(false);
  const [dayData, setDayData] = useState<DayDetailData | null>(null);
  const [dayLoading, setDayLoading] = useState(false);
  const [dayError, setDayError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('months');
  const [selectedMonth, setSelectedMonth] = useState<number | null>(null);
  const [monthOrigin, setMonthOrigin] = useState<DayOriginRect | null>(null);
  const [monthIsCurrent, setMonthIsCurrent] = useState(false);
  const [monthData, setMonthData] = useState<MonthDetailData | null>(null);
  const [monthLoading, setMonthLoading] = useState(false);
  const [monthError, setMonthError] = useState<string | null>(null);
  const [yearPickerOpen, setYearPickerOpen] = useState(false);
  const [modalityOptions, setModalityOptions] = useState<{ label: string; count: number }[]>([]);
  const [selectedModalities, setSelectedModalities] = useState<string[]>([]);
  const yearPickerRef = useRef<HTMLDivElement | null>(null);
  const yearPickerBtnRef = useRef<HTMLButtonElement | null>(null);
  const yearListRef = useRef<HTMLDivElement | null>(null);
  const [yearPickerPos, setYearPickerPos] = useState({ top: 0, left: 0, width: 176 });

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    if (!canAccessCustomerClub()) {
      navigate('/admin/dashboard');
    }
  }, [navigate]);

  const authHeaders = useCallback((): HeadersInit => {
    const token = localStorage.getItem('admin_token') || '';
    return { Authorization: `Bearer ${token}` };
  }, []);

  const serviceTypesQuery = useMemo(
    () => (selectedModalities.length ? selectedModalities.join('|') : ''),
    [selectedModalities]
  );

  const loadModalities = useCallback(async () => {
    try {
      const apiBaseUrl = getApiBaseUrl();
      const fromDate = `${year}/01/01`;
      const toDate = `${year}/12/29`;
      const params = new URLSearchParams({
        from_date: fromDate,
        to_date: toDate,
        limit: '80',
      });
      const res = await fetch(`${apiBaseUrl}/admin/customer-club/services?${params}`, {
        headers: authHeaders(),
      });
      if (!res.ok) return;
      const json = await res.json();
      const types = Array.isArray(json.types) ? json.types : [];
      setModalityOptions(
        types
          .filter((t: { label?: string }) => t?.label)
          .map((t: { label: string; count?: number }) => ({
            label: t.label,
            count: Number(t.count) || 0,
          }))
      );
      setSelectedModalities((prev) =>
        prev.filter((label) => types.some((t: { label?: string }) => t.label === label))
      );
    } catch {
      /* فهرست مودالیتی اختیاری است */
    }
  }, [authHeaders, year]);

  useEffect(() => {
    void loadModalities();
  }, [loadModalities]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const apiBaseUrl = getApiBaseUrl();
      const params = new URLSearchParams({ year: String(year) });
      if (serviceTypesQuery) params.set('service_types', serviceTypesQuery);
      const res = await fetch(`${apiBaseUrl}/admin/customer-club/watchdog?${params}`, {
        headers: authHeaders(),
      });
      if (res.status === 401) {
        navigate('/admin/login');
        return;
      }
      if (res.status === 403) {
        navigate('/admin/dashboard');
        return;
      }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(typeof body.detail === 'string' ? body.detail : 'خطا در دریافت دیده‌بان');
      }
      const json = (await res.json()) as WatchdogResponse;
      setData(json);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'خطا در بارگذاری');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [authHeaders, navigate, serviceTypesQuery, year]);

  useEffect(() => {
    void load();
  }, [load]);

  const today = data?.today || moment().format('jYYYY/jMM/jDD');
  const dayStats = useMemo(() => {
    const raw = data?.days || {};
    const out: Record<string, WatchdogDayStats> = {};
    for (const [date, value] of Object.entries(raw)) {
      out[date] = normalizeDayStats(value);
    }
    return out;
  }, [data?.days]);
  const maxCount = useMemo(
    () => Math.max(0, ...Object.values(dayStats).map((s) => s.count)),
    [dayStats]
  );
  const monthStats = useMemo(
    () => aggregateMonthStats(dayStats, year),
    [dayStats, year]
  );
  const maxMonthCount = useMemo(
    () => Math.max(0, ...Object.values(monthStats).map((s) => s.count)),
    [monthStats]
  );
  const earliest = data?.earliest_year ?? 1392;
  const latest = data?.current_year ?? moment().jYear();
  const currentMonth = Number(today.split('/')[1] || '1');
  const isFutureMonth = useCallback(
    (monthIndex: number) => {
      if (year > latest) return true;
      if (year < latest) return false;
      return monthIndex > currentMonth;
    },
    [currentMonth, latest, year]
  );

  const yearOptions = useMemo(() => {
    const out: number[] = [];
    for (let y = latest; y >= earliest; y -= 1) {
      out.push(y);
    }
    return out;
  }, [earliest, latest]);

  useLayoutEffect(() => {
    if (!yearPickerOpen) return;
    const updatePos = () => {
      const rect = yearPickerBtnRef.current?.getBoundingClientRect();
      if (!rect) return;
      const width = Math.max(rect.width, 176);
      const pad = 8;
      let left = rect.right - width;
      left = Math.max(pad, Math.min(left, window.innerWidth - width - pad));
      setYearPickerPos({
        top: rect.bottom + 8,
        left,
        width,
      });
    };
    updatePos();
    window.addEventListener('resize', updatePos);
    window.addEventListener('scroll', updatePos, true);
    return () => {
      window.removeEventListener('resize', updatePos);
      window.removeEventListener('scroll', updatePos, true);
    };
  }, [yearPickerOpen]);

  useEffect(() => {
    if (!yearPickerOpen) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        !yearPickerRef.current?.contains(target) &&
        !yearPickerBtnRef.current?.contains(target)
      ) {
        setYearPickerOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setYearPickerOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    const scrollTimer = window.setTimeout(() => {
      yearListRef.current
        ?.querySelector('[data-selected="true"]')
        ?.scrollIntoView({ block: 'nearest' });
    }, 0);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
      window.clearTimeout(scrollTimer);
    };
  }, [yearPickerOpen, year]);

  const months = useMemo(
    () =>
      MONTH_NAMES.map((name, idx) => ({
        name,
        cells: buildMonthCells(year, idx, dayStats, today),
      })),
    [dayStats, today, year]
  );

  const showTip = (
    e: React.MouseEvent<HTMLButtonElement>,
    date: string,
    stats: WatchdogDayStats,
    isTodayDay: boolean
  ) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTip({
      kind: 'day',
      x: rect.left + rect.width / 2,
      y: rect.top - 8,
      date,
      stats,
      isToday: isTodayDay,
      peakCount: data?.peak_count ?? maxCount,
    });
  };

  const showMonthTip = (
    e: React.MouseEvent<HTMLButtonElement>,
    monthIndex: number,
    stats: WatchdogDayStats,
    isCurrentMonth: boolean
  ) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTip({
      kind: 'month',
      x: rect.left + rect.width / 2,
      y: rect.top - 8,
      date: `${year}/${String(monthIndex).padStart(2, '0')}`,
      title: MONTH_NAMES[monthIndex - 1],
      subtitle: toPersianDigits(year),
      stats,
      isCurrentMonth,
      peakCount: maxMonthCount,
    });
  };

  const openDay = async (
    date: string,
    isTodayDay: boolean,
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setTip(null);
    const rect = event.currentTarget.getBoundingClientRect();
    setDayOrigin({
      top: rect.top,
      left: rect.left,
      width: rect.width,
      height: rect.height,
    });
    setDayIsToday(isTodayDay);
    setSelectedDate(date);
    setDayData(null);
    setDayError(null);
    setDayLoading(true);
    try {
      const apiBaseUrl = getApiBaseUrl();
      const params = new URLSearchParams({ date });
      if (serviceTypesQuery) params.set('service_types', serviceTypesQuery);
      const res = await fetch(
        `${apiBaseUrl}/admin/customer-club/watchdog/day?${params}`,
        { headers: authHeaders() }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(typeof body.detail === 'string' ? body.detail : 'خطا در دریافت جزئیات روز');
      }
      setDayData((await res.json()) as DayDetailData);
    } catch (err: unknown) {
      setDayError(err instanceof Error ? err.message : 'خطا در دریافت جزئیات روز');
    } finally {
      setDayLoading(false);
    }
  };

  const closeDay = useCallback(() => {
    setSelectedDate(null);
    setDayOrigin(null);
    setDayData(null);
    setDayError(null);
    setDayIsToday(false);
  }, []);

  const openMonth = async (
    monthIndex: number,
    isCurrentMonth: boolean,
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setTip(null);
    const rect = event.currentTarget.getBoundingClientRect();
    setMonthOrigin({
      top: rect.top,
      left: rect.left,
      width: rect.width,
      height: rect.height,
    });
    setMonthIsCurrent(isCurrentMonth);
    setSelectedMonth(monthIndex);
    setMonthData(null);
    setMonthError(null);
    setMonthLoading(true);
    try {
      const apiBaseUrl = getApiBaseUrl();
      const params = new URLSearchParams({
        year: String(year),
        month: String(monthIndex),
      });
      if (serviceTypesQuery) params.set('service_types', serviceTypesQuery);
      const res = await fetch(
        `${apiBaseUrl}/admin/customer-club/watchdog/month?${params}`,
        { headers: authHeaders() }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(typeof body.detail === 'string' ? body.detail : 'خطا در دریافت جزئیات ماه');
      }
      setMonthData((await res.json()) as MonthDetailData);
    } catch (err: unknown) {
      setMonthError(err instanceof Error ? err.message : 'خطا در دریافت جزئیات ماه');
    } finally {
      setMonthLoading(false);
    }
  };

  const closeMonth = useCallback(() => {
    setSelectedMonth(null);
    setMonthOrigin(null);
    setMonthData(null);
    setMonthError(null);
    setMonthIsCurrent(false);
  }, []);

  const toggleModality = (label: string) => {
    setSelectedModalities((prev) =>
      prev.includes(label) ? prev.filter((x) => x !== label) : [...prev, label]
    );
    closeDay();
    closeMonth();
    setTip(null);
  };

  const clearModalities = () => {
    setSelectedModalities([]);
    closeDay();
    closeMonth();
    setTip(null);
  };

  const switchViewMode = (mode: ViewMode) => {
    setViewMode(mode);
    setTip(null);
    closeDay();
    closeMonth();
  };

  return (
    <div className="min-w-0 max-w-full space-y-5 overflow-x-hidden text-base text-gray-900" style={{ direction: 'rtl' }}>
      <section className="relative rounded-2xl bg-gradient-to-l from-slate-800 via-slate-700 to-indigo-900 px-5 py-5 text-white shadow-lg sm:px-6">
        <div
          className="pointer-events-none absolute -left-10 -top-10 h-40 w-40 rounded-full bg-white/10 blur-2xl"
          aria-hidden
        />
        <div className="relative flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-xl font-bold tracking-tight sm:text-2xl">دیده‌بان</h1>
            <p className="mt-1.5 text-sm text-slate-200/90">
              تقویم پذیرش HIS — با فیلتر مودالیتی، بین نمای ماه و روز جابه‌جا شوید و جزئیات
              نقدی/بیمه را ببینید.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              disabled={year <= earliest || loading}
              onClick={() => setYear((y) => y - 1)}
              className="rounded-lg border border-white/25 bg-white/10 px-3 py-2 text-sm hover:bg-white/20 disabled:opacity-40"
            >
              سال قبل
            </button>
            <button
              type="button"
              onClick={() => setYear(latest)}
              className="rounded-lg border border-white/25 bg-white/10 px-3 py-2 text-sm hover:bg-white/20"
            >
              امسال
            </button>
            <button
              type="button"
              disabled={year >= latest || loading}
              onClick={() => setYear((y) => y + 1)}
              className="rounded-lg border border-white/25 bg-white/10 px-3 py-2 text-sm hover:bg-white/20 disabled:opacity-40"
            >
              سال بعد
            </button>
            <div className="relative mr-1">
              <button
                ref={yearPickerBtnRef}
                type="button"
                onClick={() => setYearPickerOpen((open) => !open)}
                aria-expanded={yearPickerOpen}
                aria-haspopup="listbox"
                aria-label="انتخاب سال"
                className={`flex min-w-[4.5rem] items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-lg font-bold tabular-nums transition ${
                  yearPickerOpen
                    ? 'border-white/40 bg-white/25 ring-2 ring-white/30'
                    : 'border-white/25 bg-white/15 hover:bg-white/25'
                }`}
              >
                {toPersianDigits(year)}
                <svg
                  className={`h-3.5 w-3.5 shrink-0 text-white/80 transition-transform ${
                    yearPickerOpen ? 'rotate-180' : ''
                  }`}
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden
                >
                  <path
                    fillRule="evenodd"
                    d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.24 4.5a.75.75 0 01-1.08 0l-4.24-4.5a.75.75 0 01.02-1.06z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </section>

      {yearPickerOpen
        ? createPortal(
            <div
              ref={yearPickerRef}
              className="fixed z-[85] overflow-hidden rounded-xl border border-white/15 bg-slate-900/95 shadow-2xl shadow-black/40 backdrop-blur-md"
              style={{
                top: yearPickerPos.top,
                left: yearPickerPos.left,
                width: yearPickerPos.width,
                direction: 'rtl',
              }}
              role="listbox"
              aria-label="لیست سال‌ها"
            >
              <div className="border-b border-white/10 px-3 py-2 text-[11px] text-slate-300">
                انتخاب سال ({toPersianDigits(earliest)} – {toPersianDigits(latest)})
              </div>
              <div ref={yearListRef} className="max-h-64 overflow-y-auto py-1">
                {yearOptions.map((y) => {
                  const selected = y === year;
                  const isCurrentYear = y === latest;
                  return (
                    <button
                      key={y}
                      type="button"
                      role="option"
                      aria-selected={selected}
                      data-selected={selected ? 'true' : undefined}
                      onClick={() => {
                        setYear(y);
                        setYearPickerOpen(false);
                      }}
                      className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-sm tabular-nums transition ${
                        selected
                          ? 'bg-indigo-500/30 font-bold text-white'
                          : 'text-slate-200 hover:bg-white/10'
                      }`}
                    >
                      <span>{toPersianDigits(y)}</span>
                      {isCurrentYear ? (
                        <span className="rounded-full bg-emerald-500/20 px-2 py-0.5 text-[10px] font-medium text-emerald-300">
                          امسال
                        </span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            </div>,
            document.body
          )
        : null}

      {modalityOptions.length > 0 ? (
        <div className="min-w-0 max-w-full overflow-hidden rounded-2xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-800">مودالیتی / نوع خدمت</p>
              <p className="text-[11px] text-gray-500">
                تقویم و آمار را محدود به دستگاه انتخاب‌شده کنید.
              </p>
            </div>
            {selectedModalities.length > 0 ? (
              <button
                type="button"
                onClick={clearModalities}
                className="shrink-0 text-xs text-indigo-600 hover:underline"
              >
                پاک کردن فیلتر
              </button>
            ) : null}
          </div>
          <div className="flex max-w-full flex-wrap gap-2">
            <button
              type="button"
              onClick={clearModalities}
              className={`rounded-full border px-3 py-1.5 text-xs font-medium transition ${
                selectedModalities.length === 0
                  ? 'border-indigo-300 bg-indigo-50 text-indigo-800'
                  : 'border-gray-200 bg-white text-gray-600 hover:border-indigo-200'
              }`}
            >
              همه
            </button>
            {modalityOptions.map((opt) => {
              const active = selectedModalities.includes(opt.label);
              return (
                <button
                  key={opt.label}
                  type="button"
                  onClick={() => toggleModality(opt.label)}
                  className={`max-w-full rounded-full border px-3 py-1.5 text-xs font-medium transition ${
                    active
                      ? 'border-indigo-400 bg-indigo-600 text-white shadow-sm'
                      : 'border-gray-200 bg-white text-gray-700 hover:border-indigo-200'
                  }`}
                >
                  <span className="inline-block max-w-[12rem] truncate align-bottom">
                    {opt.label}
                  </span>
                  <span
                    className={`mr-1.5 tabular-nums ${
                      active ? 'text-indigo-100' : 'text-gray-400'
                    }`}
                  >
                    {toPersianDigits(opt.count.toLocaleString('en-US'))}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      {data && !loading ? (
        <div className="flex flex-wrap gap-3 text-sm text-gray-600">
          <span className="rounded-full bg-slate-100 px-3 py-1 tabular-nums">
            مجموع پذیرش: {toPersianDigits(data.total.toLocaleString('en-US'))}
          </span>
          {selectedModalities.length > 0 ? (
            <span className="rounded-full bg-violet-50 px-3 py-1 text-violet-800">
              فیلتر: {selectedModalities.join('، ')}
            </span>
          ) : null}
          {data.peak_day ? (
            <span className="rounded-full bg-indigo-50 px-3 py-1 text-indigo-800 tabular-nums">
              اوج: {toPersianDigits(data.peak_day)} — {toPersianDigits(data.peak_count)} پذیرش
            </span>
          ) : null}
        </div>
      ) : null}

      {error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      ) : null}

      {loading ? (
        <LoadingSpinner text="در حال بارگذاری تقویم پذیرش‌ها..." />
      ) : (
        <section className="min-w-0 max-w-full overflow-hidden rounded-2xl border border-gray-200 bg-white p-3 shadow-sm sm:p-5">
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 pb-4">
            <div>
              <p className="text-sm font-bold text-gray-900">سطح زوم</p>
            </div>
            <WatchdogViewSwitcher value={viewMode} onChange={switchViewMode} />
          </div>

          <div className="w-full min-w-0 overflow-hidden" dir="ltr">
            <div
              className="flex transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] will-change-transform"
              style={{
                transform:
                  viewMode === 'months' ? 'translate3d(0,0,0)' : 'translate3d(-100%,0,0)',
              }}
            >
              <div className="w-full min-w-0 shrink-0 grow-0 basis-full px-0.5" dir="rtl">
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
                  {MONTH_NAMES.map((name, idx) => {
                    const monthIndex = idx + 1;
                    const stats = monthStats[monthIndex] || emptyDayStats();
                    const muted = isFutureMonth(monthIndex);
                    const isCurrentMonthTile = year === latest && monthIndex === currentMonth;
                    return (
                      <button
                        key={name}
                        type="button"
                        disabled={muted}
                        onClick={(e) => {
                          if (!muted) void openMonth(monthIndex, isCurrentMonthTile, e);
                        }}
                        onMouseEnter={(e) => showMonthTip(e, monthIndex, stats, isCurrentMonthTile)}
                        onMouseMove={(e) => showMonthTip(e, monthIndex, stats, isCurrentMonthTile)}
                        onMouseLeave={() => setTip(null)}
                        onFocus={(e) =>
                          showMonthTip(
                            e as unknown as React.MouseEvent<HTMLButtonElement>,
                            monthIndex,
                            stats,
                            isCurrentMonthTile
                          )
                        }
                        onBlur={() => setTip(null)}
                        className={`relative flex min-h-[6.5rem] flex-col items-center justify-center rounded-2xl border px-3 py-4 text-center transition hover:ring-2 hover:ring-emerald-300 focus:outline-none focus:ring-2 focus:ring-emerald-400 disabled:cursor-default ${
                          selectedMonth === monthIndex ? 'invisible' : ''
                        } ${
                          isCurrentMonthTile
                            ? 'border-emerald-200 bg-emerald-500 font-bold text-white shadow-md shadow-emerald-200/50'
                            : muted
                              ? 'border-gray-100 bg-gray-50 text-gray-300'
                              : 'cursor-pointer border-gray-200 bg-white text-gray-800 hover:border-indigo-200'
                        }`}
                        style={
                          isCurrentMonthTile || muted
                            ? undefined
                            : heatStyle(stats.count, maxMonthCount)
                        }
                        aria-label={`${name} — ${toPersianDigits(stats.count)} پذیرش`}
                      >
                        <span className="text-sm font-bold sm:text-base">{name}</span>
                        <span
                          className={`mt-2 text-2xl font-bold tabular-nums ${
                            isCurrentMonthTile ? 'text-white' : 'text-indigo-700'
                          }`}
                        >
                          {toPersianDigits(stats.count)}
                        </span>
                        <span
                          className={`mt-1 text-[10px] ${
                            isCurrentMonthTile ? 'text-emerald-100' : 'text-gray-500'
                          }`}
                        >
                          پذیرش
                        </span>
                        {stats.total_amount > 0 && !muted ? (
                          <span className="mt-2 inline-flex h-1.5 w-full max-w-[5rem] overflow-hidden rounded-full bg-black/10">
                            <span
                              className="bg-amber-400"
                              style={{
                                width: `${Math.max(4, (stats.naghdi / stats.total_amount) * 100)}%`,
                              }}
                            />
                            <span
                              className="bg-sky-400"
                              style={{
                                width: `${Math.max(4, (stats.bime / stats.total_amount) * 100)}%`,
                              }}
                            />
                          </span>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="w-full min-w-0 shrink-0 grow-0 basis-full px-0.5" dir="rtl">
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-4">
                  {months.map((month) => (
                    <div key={month.name} className="min-w-0">
                      <h3 className="mb-2 text-sm font-bold text-blue-700">{month.name}</h3>
                      <div className="mb-1 grid grid-cols-7 gap-px text-center text-[10px] font-medium text-gray-400">
                        {WEEKDAYS.map((w) => (
                          <span key={`${month.name}-${w}`}>{w}</span>
                        ))}
                      </div>
                      <div className="grid grid-cols-7 gap-px">
                        {month.cells.map((cell, idx) => {
                          if (cell.kind === 'empty') {
                            return <div key={`e-${idx}`} className="aspect-square" />;
                          }
                          const muted = cell.isFuture;
                          const stats = dayStats[cell.date] || emptyDayStats();
                          return (
                            <button
                              key={cell.date}
                              type="button"
                              disabled={muted}
                              onClick={(e) => {
                                if (!muted) void openDay(cell.date, cell.isToday, e);
                              }}
                              onMouseEnter={(e) => showTip(e, cell.date, stats, cell.isToday)}
                              onMouseMove={(e) => showTip(e, cell.date, stats, cell.isToday)}
                              onMouseLeave={() => setTip(null)}
                              className={`relative flex aspect-square items-center justify-center rounded-full text-[11px] tabular-nums transition hover:ring-2 hover:ring-emerald-300 focus:outline-none focus:ring-2 focus:ring-emerald-400 disabled:cursor-default ${
                                selectedDate === cell.date ? 'invisible' : ''
                              } ${
                                cell.isToday
                                  ? 'bg-emerald-500 font-bold text-white shadow-sm'
                                  : muted
                                    ? 'text-gray-300'
                                    : 'cursor-pointer text-gray-800'
                              }`}
                              style={cell.isToday || muted ? undefined : heatStyle(cell.count, maxCount)}
                              aria-label={`${toPersianDigits(cell.date)} — ${toPersianDigits(cell.count)} پذیرش`}
                            >
                              {toPersianDigits(cell.day)}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-3 border-t border-gray-100 pt-4 text-xs text-gray-500">
            <span>شدت رنگ ≈ تعداد پذیرش</span>
            <span className="inline-flex items-center gap-1">
              <span className="h-3 w-3 rounded-full bg-emerald-500" />{' '}
              {viewMode === 'days' ? 'امروز' : 'ماه جاری'}
            </span>
            <span className="inline-flex items-center gap-2">
              کم
              <span className="h-2.5 w-16 rounded-full bg-gradient-to-l from-blue-600/70 to-blue-600/10" />
              زیاد
            </span>
          </div>
        </section>
      )}

      <WatchdogDayModal
        open={Boolean(selectedDate)}
        date={selectedDate}
        dayLabel={selectedDate ? String(Number(selectedDate.split('/')[2])) : undefined}
        origin={dayOrigin}
        isToday={dayIsToday}
        loading={dayLoading}
        error={dayError}
        data={dayData}
        serviceTypesQuery={serviceTypesQuery}
        onClose={closeDay}
      />

      <WatchdogMonthModal
        open={Boolean(selectedMonth)}
        year={selectedMonth ? year : null}
        month={selectedMonth}
        monthLabel={selectedMonth ? MONTH_NAMES[selectedMonth - 1] : undefined}
        origin={monthOrigin}
        isCurrentMonth={monthIsCurrent}
        loading={monthLoading}
        error={monthError}
        data={monthData}
        serviceTypesQuery={serviceTypesQuery}
        onClose={closeMonth}
      />

      <WatchdogDayTooltip tip={tip && !selectedDate && !selectedMonth ? tip : null} />
    </div>
  );
};

export default AdminCustomerClubWatchdog;
